/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable lingui/no-unlocalized-strings */

import { t } from "@lingui/macro";
import type { ComboboxOption } from "@reactive-resume/ui";
import { Button, Combobox, Label, Slider, Switch } from "@reactive-resume/ui";
import { cn, fonts } from "@reactive-resume/utils";
import { useCallback, useEffect, useState } from "react";
import webfontloader from "webfontloader";

import { useResumeStore } from "@/client/stores/resume";

import { SectionIcon } from "../../../shared";

const localFonts = ["Josefin Sans", "Arial", "Cambria", "Garamond", "Times New Roman"];

const fontSuggestions = [
  ...localFonts,
  "IBM Plex Sans",
  "IBM Plex Serif",
  "Lato",
  "Lora",
  "Merriweather",
  "Open Sans",
  "Playfair Display",
  "PT Sans",
  "PT Serif",
  "Roboto Condensed",
];

const families = fonts.map((font) => ({
  value: font.family,
  label: font.family,
})) satisfies ComboboxOption[];

families.push(...localFonts.map((font) => ({ value: font, label: font })));

export const TypographySection = () => {
  const [subsets, setSubsets] = useState<ComboboxOption[]>([]);
  const [variants, setVariants] = useState<ComboboxOption[]>([]);

  const setValue = useResumeStore((state) => state.setValue);
  const typography = useResumeStore((state) => state.resume.data.metadata.typography);
  const defaultVariant = typography.font.defaultVariant ?? "regular";

  const loadFontSuggestions = useCallback(() => {
    for (const font of fontSuggestions) {
      if (localFonts.includes(font)) continue;

      webfontloader.load({
        events: false,
        classes: false,
        google: { families: [font], text: font },
      });
    }
  }, [fontSuggestions]);

  useEffect(() => {
    loadFontSuggestions();
  }, []);

  useEffect(() => {
    const subsets = fonts.find((font) => font.family === typography.font.family)?.subsets ?? [];
    if (subsets.length > 0) {
      setSubsets(subsets.map((subset) => ({ value: subset, label: subset })));
    }

    const variants = fonts.find((font) => font.family === typography.font.family)?.variants ?? [];
    if (variants.length > 0) {
      setVariants(variants.map((variant) => ({ value: variant, label: variant })));
    }
  }, [typography.font.family]);

  return (
    <section id="typography" className="grid gap-y-8">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-x-4">
          <SectionIcon id="typography" size={18} name={t`Typography`} />
          <h3 className="line-clamp-1 text-xl font-bold lg:text-xl/loose">{t`Typography`}</h3>
        </div>
      </header>

      <main className="grid gap-y-6 p-1">
        <div className="grid grid-cols-2 gap-4">
          {fontSuggestions
            .sort((a, b) => a.localeCompare(b))
            .map((font) => (
              <Button
                key={font}
                variant="outline"
                style={{ fontFamily: font }}
                disabled={typography.font.family === font}
                className={cn(
                  "flex h-12 items-center justify-center overflow-hidden rounded border bg-background text-center text-xs text-foreground transition-colors focus:outline-none focus:ring-1 disabled:opacity-100 lg:text-sm",
                  typography.font.family === font && "ring-1",
                  "border-acade-secondary-200 ring-acade-secondary-500 hover:bg-acade-secondary-100 dark:border-acade-secondary-900",
                  "disabled:bg-acade-secondary-100/50 dark:disabled:bg-acade-secondary-900/50",
                )}
                onClick={() => {
                  setValue("metadata.typography.font.family", font);
                  setValue("metadata.typography.font.subset", "latin");
                  setValue("metadata.typography.font.variants", ["regular"]);
                  setValue("metadata.typography.font.defaultVariant", "regular");
                }}
              >
                {font}
              </Button>
            ))}
        </div>

        <div className="space-y-1.5">
          <Label>{t`Font Family`}</Label>
          <Combobox
            options={families.sort((a, b) => a.label.localeCompare(b.label))}
            value={typography.font.family}
            searchPlaceholder={t`Search for a font family`}
            onValueChange={(value) => {
              setValue("metadata.typography.font.family", value);
              setValue("metadata.typography.font.subset", "latin");
              setValue("metadata.typography.font.variants", ["regular"]);
              setValue("metadata.typography.font.defaultVariant", "regular");
            }}
          />
        </div>

        <div className="grid grid-cols-2 gap-x-4">
          <div className="space-y-1.5">
            <Label>{t`Font Subset`}</Label>
            <Combobox
              options={subsets}
              value={typography.font.subset}
              searchPlaceholder={t`Search for a font subset`}
              onValueChange={(value) => {
                setValue("metadata.typography.font.subset", value);
              }}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t`Font Variants`}</Label>
            <Combobox
              multiple
              options={variants}
              value={typography.font.variants}
              searchPlaceholder={t`Search for a font variant`}
              onValueChange={(value) => {
                const selected = value.includes(defaultVariant) ? value : [...value, defaultVariant];
                setValue("metadata.typography.font.variants", selected);
              }}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Variante de base</Label>
          <Combobox
            options={variants}
            value={defaultVariant}
            searchPlaceholder="Rechercher une variante"
            onValueChange={(value) => {
              setValue("metadata.typography.font.defaultVariant", value);

              if (!typography.font.variants.includes(value)) {
                setValue("metadata.typography.font.variants", [...typography.font.variants, value]);
              }
            }}
          />
        </div>

        <div className="space-y-3 rounded border border-acade-secondary-200 p-3 dark:border-acade-secondary-900">
          <Label>Nom</Label>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <div className="space-y-1.5">
              <Label>Taille</Label>
              <div className="flex items-center gap-x-3 py-1">
                <Slider
                  min={14}
                  max={48}
                  step={1}
                  value={[typography.nameSize ?? 24]}
                  onValueChange={(value) => {
                    setValue("metadata.typography.nameSize", value[0]);
                  }}
                />

                <span className="min-w-10 text-right text-base font-bold">
                  {typography.nameSize ?? 24}px
                </span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Interligne</Label>
              <div className="flex items-center gap-x-3 py-1">
                <Slider
                  min={0.8}
                  max={2}
                  step={0.05}
                  value={[typography.nameLineHeight ?? 1.2]}
                  onValueChange={(value) => {
                    setValue("metadata.typography.nameLineHeight", value[0]);
                  }}
                />

                <span className="min-w-10 text-right text-base font-bold">
                  {typography.nameLineHeight ?? 1.2}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3 rounded border border-acade-secondary-200 p-3 dark:border-acade-secondary-900">
          <Label>Poste</Label>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <div className="space-y-1.5">
              <Label>Taille</Label>
              <div className="flex items-center gap-x-3 py-1">
                <Slider
                  min={10}
                  max={32}
                  step={1}
                  value={[typography.headlineSize ?? 16]}
                  onValueChange={(value) => {
                    setValue("metadata.typography.headlineSize", value[0]);
                  }}
                />

                <span className="min-w-10 text-right text-base font-bold">
                  {typography.headlineSize ?? 16}px
                </span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Interligne</Label>
              <div className="flex items-center gap-x-3 py-1">
                <Slider
                  min={0.8}
                  max={2}
                  step={0.05}
                  value={[typography.headlineLineHeight ?? 1.25]}
                  onValueChange={(value) => {
                    setValue("metadata.typography.headlineLineHeight", value[0]);
                  }}
                />

                <span className="min-w-10 text-right text-base font-bold">
                  {typography.headlineLineHeight ?? 1.25}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>{t`Font Size`}</Label>
          <div className="flex items-center gap-x-4 py-1">
            <Slider
              min={6}
              max={18}
              step={0.05}
              value={[typography.font.size]}
              onValueChange={(value) => {
                setValue("metadata.typography.font.size", value[0]);
              }}
            />

            <span className="text-base font-bold">{typography.font.size}</span>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>{t`Line Height`}</Label>
          <div className="flex items-center gap-x-4 py-1">
            <Slider
              min={0}
              max={3}
              step={0.05}
              value={[typography.lineHeight]}
              onValueChange={(value) => {
                setValue("metadata.typography.lineHeight", value[0]);
              }}
            />

            <span className="text-base font-bold">{typography.lineHeight}</span>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>{t`Options`}</Label>

          <div className="flex items-center gap-x-4 py-1">
            <Switch
              id="metadata.typography.hideIcons"
              checked={typography.hideIcons}
              onCheckedChange={(checked) => {
                setValue("metadata.typography.hideIcons", checked);
              }}
            />
            <Label htmlFor="metadata.typography.hideIcons">{t`Hide Icons`}</Label>
          </div>

          <div className="flex items-center gap-x-4 py-1">
            <Switch
              id="metadata.typography.underlineLinks"
              checked={typography.underlineLinks}
              onCheckedChange={(checked) => {
                setValue("metadata.typography.underlineLinks", checked);
              }}
            />
            <Label htmlFor="metadata.typography.underlineLinks">{t`Underline Links`}</Label>
          </div>
        </div>
      </main>
    </section>
  );
};
